import { DateTime } from 'luxon';

import { viewSettingsStore } from '@/features/viewSettings';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { fromISOtoISODate } from '@/utils/fromISOtoISODate';

import { TimesheetData, WeeklyWorkLog } from '../../types';

export interface OptimisticUpdateDependencies {
  /** Получить текущие данные таймшита */
  getCurrentTimesheetData: () => Promise<TimesheetData | null>;
  /** Обновить данные таймшита */
  updateTimesheetData: (data: TimesheetData) => void;
  /** Получить ISO дату начала текущей недели */
  getCurrentPeriodStartISO: () => string | null;
  /** Переключиться на указанную дату */
  setCurrentDate: (date: DateTime) => void;
  /** Установить ID последнего добавленного лога */
  setLastAddId: (id: string) => void;
  /** Добавить очередь из задачи в фильтры */
  addQueueFromTaskKey: (taskKey: string) => void;
  /** Удалить очередь из фильтров, если её больше нет в данных */
  removeQueueIfNotExists: (queue: string) => void;
}

export class OptimisticLogUpdateModel {
  constructor(private dependencies: OptimisticUpdateDependencies) {}

  /**
   * Оптимистично добавляет лог в клиентское состояние
   */
  addLog(
    newLog: WeeklyWorkLog,
    startDate: string | undefined,
    logDate: string,
    durationMinutes: number,
  ) {
    this.dependencies.setLastAddId(newLog.id);
    const { view } = viewSettingsStore;

    if (!startDate) return;

    const createdPeriodStart = DateTime.fromISO(startDate).startOf(view).toISODate();
    const currentPeriodStart = this.dependencies.getCurrentPeriodStartISO();
    const isCurrentWeek = createdPeriodStart === currentPeriodStart;

    if (isCurrentWeek) {
      this._addToCurrentPeriod(newLog, logDate, durationMinutes);
    } else {
      this._switchPeriodAndAdd(startDate, newLog, logDate, durationMinutes);
    }
  }

  /**
   * Оптимистично удаляет лог из клиентского состояния
   */
  async removeLog(logId: string, logDate: string, durationMinutes: number) {
    const current = await this.dependencies.getCurrentTimesheetData();

    if (!current) return;

    const dayLogs = current.days[logDate];

    if (!dayLogs) return;

    const logToRemove = dayLogs.find((log) => log.id === logId);

    if (!logToRemove) return;

    const queue = this._extractQueueFromTaskKey(logToRemove.taskKey);

    const updatedDayLogs = dayLogs.filter((log) => log.id !== logId);
    const updatedDays = { ...current.days };

    updatedDays[logDate] = updatedDayLogs;

    const totalMinutes = current.totalMinutes - durationMinutes;

    this.dependencies.updateTimesheetData({
      ...current,
      days: updatedDays,
      totalMinutes,
      totalFormatted: formatDurationRussian(totalMinutes),
    });

    if (queue) {
      this.dependencies.removeQueueIfNotExists(queue);
    }
  }

  /**
   * Извлекает очередь из ключа задачи
   */
  private _extractQueueFromTaskKey(taskKey: string): string | null {
    const match = taskKey.match(/^([A-Z]+)-\d+/);

    return match ? match[1] : null;
  }

  /**
   * Оптимистично обновляет существующий лог
   */
  async updateLog({
    updatedLog,
    oldLogDate,
    newLogDate,
    oldDurationMinutes,
    newDurationMinutes,
  }: {
    updatedLog: WeeklyWorkLog;
    oldLogDate: string;
    newLogDate: string;
    oldDurationMinutes: number;
    newDurationMinutes: number;
  }) {
    const current = await this.dependencies.getCurrentTimesheetData();

    if (!current) return;

    // Находим старый лог, чтобы получить его taskKey
    const oldLog = current.days[oldLogDate]?.find((log) => log.id === updatedLog.id);
    const oldQueue = oldLog ? this._extractQueueFromTaskKey(oldLog.taskKey) : null;
    const newQueue = this._extractQueueFromTaskKey(updatedLog.taskKey);

    // Сначала добавляем новую очередь в selectedKeys (если фильтры активны)
    // Это нужно сделать до обновления данных, чтобы фильтрация включила новую задачу
    if (newQueue) {
      this.dependencies.addQueueFromTaskKey(updatedLog.taskKey);
    }

    const updatedDays = { ...current.days };

    // Удоляем старый лог
    if (updatedDays[oldLogDate]) {
      updatedDays[oldLogDate] = updatedDays[oldLogDate].filter((log) => log.id !== updatedLog.id);
    }

    // Добовляем обновленный лог
    updatedDays[newLogDate] = [...(updatedDays[newLogDate] || []), updatedLog];
    // Надо пощитать новое время
    const totalMinutes = current.totalMinutes - oldDurationMinutes + newDurationMinutes;

    this.dependencies.updateTimesheetData({
      ...current,
      days: updatedDays,
      totalMinutes,
      totalFormatted: formatDurationRussian(totalMinutes),
    });

    // Удаляем старую очередь, если она изменилась и её больше нет в данных
    if (oldQueue && oldQueue !== newQueue) {
      this.dependencies.removeQueueIfNotExists(oldQueue);
    }
  }

  /**
   * Добавляет лог в данные текущей недели
   */
  private async _addToCurrentPeriod(
    newLog: WeeklyWorkLog,
    logDate: string,
    durationMinutes: number,
  ) {
    const current = await this.dependencies.getCurrentTimesheetData();

    if (!current) return;

    const processedLogDate = DateTime.fromISO(logDate).toISODate();

    if (!processedLogDate) return;

    this.dependencies.addQueueFromTaskKey(newLog.taskKey);

    const updatedDays = { ...current.days };
    updatedDays[processedLogDate] = [...(updatedDays[processedLogDate] || []), newLog];

    const totalMinutes = current.totalMinutes + durationMinutes;

    this.dependencies.updateTimesheetData({
      ...current,
      days: updatedDays,
      totalMinutes,
      totalFormatted: formatDurationRussian(totalMinutes),
    });
  }

  /**
   * Переключается на другую неделю и добавляет лог
   */
  private async _switchPeriodAndAdd(
    startDate: string,
    newLog: WeeklyWorkLog,
    logDate: string,
    durationMinutes: number,
  ) {
    this.dependencies.setCurrentDate(DateTime.fromISO(startDate));

    const reloaded = await this.dependencies.getCurrentTimesheetData();
    const processedLogDate = fromISOtoISODate(logDate);

    if (!processedLogDate) return;

    if (!reloaded || reloaded.days[processedLogDate]?.some((l) => l.id === newLog.id)) {
      return;
    }

    this.dependencies.addQueueFromTaskKey(newLog.taskKey);

    const updatedDays = { ...reloaded.days };

    updatedDays[processedLogDate] = [...(updatedDays[processedLogDate] || []), newLog];

    const totalMinutes = reloaded.totalMinutes + durationMinutes;

    this.dependencies.updateTimesheetData({
      ...reloaded,
      days: updatedDays,
      totalMinutes,
      totalFormatted: formatDurationRussian(totalMinutes),
    });
  }
}
